import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { HeadlineStatsCard } from "./HeadlineStatsCard";
import { HeadlineAIGenerator } from "./HeadlineAIGenerator";
import { HeadlineTagStats } from "./HeadlineTagStats";
import { HeadlineDefaultTagManager } from "./HeadlineDefaultTagManager";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HeadlineVariantRow } from "./HeadlineVariantRow";
import { ArrowUpDown, ArrowUp, ArrowDown, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface HeadlineStats {
  id: string;
  headline_text: string;
  subtitle: string | null;
  button_text: string | null;
  is_active: boolean;
  impressions: number;
  conversions: number;
  conversion_rate: number;
  quiz_only_conversions: number;
  quiz_only_conversion_rate: number;
  lead_conversions: number;
  lead_conversion_rate: number;
  b2c_exits: number;
  b2c_exit_rate: number;
  cta_clicks: number;
  cta_click_rate: number;
  notes: string | null;
  tags: string[] | null;
}

interface TagStats {
  campaign_tag: string;
  impressions: number;
  conversions: number;
  conversion_rate: number;
  quiz_only_conversions: number;
  quiz_only_conversion_rate: number;
  lead_conversions: number;
  lead_conversion_rate: number;
  b2c_exits: number;
  b2c_exit_rate: number;
  cta_clicks: number;
  cta_click_rate: number;
}

export const HeadlineVariantsManager = () => {
  const [stats, setStats] = useState<HeadlineStats[]>([]);
  const [tagStats, setTagStats] = useState<TagStats[]>([]);
  const [adminStats, setAdminStats] = useState({
    impressions: 0,
    conversions: 0,
    quizOnly: 0,
    leads: 0,
    b2cExits: 0,
    ctaClicks: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [selectedTag, setSelectedTag] = useState<string>("all");
  const [sortField, setSortField] = useState<'impressions' | 'conversions' | 'conversion_rate' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const { toast } = useToast();
  const topScrollRef = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);

  const fetchStats = async () => {
    try {
      const [statsResult, tagStatsResult, adminAssignments, adminConversions] = await Promise.all([
        supabase.from('headline_stats').select('*').returns<HeadlineStats[]>(),
        supabase.from('headline_tag_stats').select('*').returns<TagStats[]>(),
        supabase.from('headline_assignments').select('id, cta_clicked').eq('ignored', true),
        supabase.from('headline_conversions').select('id, quiz_submission_id, lead_submission_id, b2c_exit').eq('ignored', true)
      ]);

      if (statsResult.error) throw statsResult.error;
      if (tagStatsResult.error) throw tagStatsResult.error;
      
      setStats(statsResult.data || []);
      setTagStats(tagStatsResult.data || []);
      
      // Calculate admin stats
      const adminImpressions = adminAssignments.data?.length || 0;
      const adminConvData = adminConversions.data || [];
      const adminAssignData = adminAssignments.data || [];
      const adminTotalConversions = adminConvData.filter(c => !c.b2c_exit).length;
      const adminQuizOnlyConversions = adminConvData.filter(c => c.quiz_submission_id && !c.lead_submission_id && !c.b2c_exit).length;
      const adminLeadConversions = adminConvData.filter(c => c.lead_submission_id && !c.b2c_exit).length;
      const adminB2CExitsCount = adminConvData.filter(c => c.b2c_exit).length;
      const adminCtaClicksCount = adminAssignData.filter(a => a.cta_clicked).length;
      
      setAdminStats({
        impressions: adminImpressions,
        conversions: adminTotalConversions,
        quizOnly: adminQuizOnlyConversions,
        leads: adminLeadConversions,
        b2cExits: adminB2CExitsCount,
        ctaClicks: adminCtaClicksCount
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się pobrać statystyk",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Get all unique tags
  const allTags = Array.from(
    new Set(
      stats.flatMap(stat => stat.tags || [])
    )
  ).sort();

  // Filter and sort stats
  const filteredAndSortedStats = (() => {
    let filtered = stats;
    
    // Apply tag filter
    if (selectedTag !== "all") {
      filtered = filtered.filter(stat => 
        stat.tags && stat.tags.includes(selectedTag)
      );
    }
    
    // Apply sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortField] || 0;
        const bValue = b[sortField] || 0;
        return sortDirection === 'desc' ? bValue - aValue : aValue - bValue;
      });
    }
    
    return filtered;
  })();

  const handleSort = (field: 'impressions' | 'conversions' | 'conversion_rate') => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      // New field, default to desc
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: 'impressions' | 'conversions' | 'conversion_rate') => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'desc' 
      ? <ArrowDown className="h-4 w-4 ml-1" />
      : <ArrowUp className="h-4 w-4 ml-1" />;
  };

  const handleEdit = async (id: string, data: {
    headline_text: string;
    subtitle: string | null;
    button_text: string | null;
    notes: string;
    tags: string[];
  }) => {
    try {
      const { error } = await supabase
        .from('headline_variants')
        .update(data)
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Sukces",
        description: "Wariant został zaktualizowany",
      });

      fetchStats();
    } catch (error) {
      console.error('Error updating variant:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się zaktualizować wariantu",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('headline_variants')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Sukces",
        description: `Wariant został ${!currentStatus ? 'aktywowany' : 'dezaktywowany'}`,
      });

      fetchStats();
    } catch (error) {
      console.error('Error toggling variant:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się zmienić statusu",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('headline_variants')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Sukces",
        description: "Wariant został usunięty",
      });

      fetchStats();
    } catch (error) {
      console.error('Error deleting variant:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się usunąć wariantu",
        variant: "destructive",
      });
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleResetStats = async () => {
    setIsResetting(true);
    try {
      const { data, error } = await supabase.functions.invoke('reset-headline-stats');

      if (error) throw error;

      toast({
        title: "Sukces",
        description: "Statystyki zostały zresetowane",
      });

      fetchStats();
    } catch (error) {
      console.error('Error resetting stats:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się zresetować statystyk",
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
      setResetConfirm(false);
    }
  };

  const totalImpressions = filteredAndSortedStats.reduce((sum, stat) => sum + stat.impressions, 0);
  const totalConversions = filteredAndSortedStats.reduce((sum, stat) => sum + stat.conversions, 0);
  const totalQuizOnly = filteredAndSortedStats.reduce((sum, stat) => sum + stat.quiz_only_conversions, 0);
  const totalLeads = filteredAndSortedStats.reduce((sum, stat) => sum + stat.lead_conversions, 0);
  const totalB2CExits = filteredAndSortedStats.reduce((sum, stat) => sum + stat.b2c_exits, 0);
  const totalCtaClicks = filteredAndSortedStats.reduce((sum, stat) => sum + stat.cta_clicks, 0);
  const averageRate = totalImpressions > 0 ? (totalConversions / totalImpressions) * 100 : 0;
  const averageQuizRate = filteredAndSortedStats.length > 0 
    ? filteredAndSortedStats.reduce((sum, s) => sum + s.quiz_only_conversion_rate, 0) / filteredAndSortedStats.length 
    : 0;
  const averageLeadRate = filteredAndSortedStats.length > 0 
    ? filteredAndSortedStats.reduce((sum, s) => sum + s.lead_conversion_rate, 0) / filteredAndSortedStats.length 
    : 0;
  const averageB2CExitRate = totalImpressions > 0 ? (totalB2CExits / totalImpressions) * 100 : 0;
  const averageCtaClickRate = filteredAndSortedStats.length > 0 
    ? filteredAndSortedStats.reduce((sum, s) => sum + s.cta_click_rate, 0) / filteredAndSortedStats.length 
    : 0;
  const topVariant = filteredAndSortedStats.length > 0 
    ? (() => {
        const top = filteredAndSortedStats.reduce((max, stat) => stat.conversion_rate > max.conversion_rate ? stat : max);
        return {
          id: top.id,
          headline: top.headline_text,
          rate: top.conversion_rate
        };
      })()
    : undefined;

  if (isLoading) {
    return <div className="p-6">Ładowanie...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <HeadlineStatsCard
            totalImpressions={totalImpressions}
            totalConversions={totalConversions}
            totalQuizOnly={totalQuizOnly}
            totalLeads={totalLeads}
            totalB2CExits={totalB2CExits}
            totalCtaClicks={totalCtaClicks}
            averageRate={averageRate}
            averageQuizRate={averageQuizRate}
            averageLeadRate={averageLeadRate}
            averageB2CExitRate={averageB2CExitRate}
            averageCtaClickRate={averageCtaClickRate}
            adminImpressions={adminStats.impressions}
            adminConversions={adminStats.conversions}
            adminQuizOnly={adminStats.quizOnly}
            adminLeads={adminStats.leads}
            adminB2CExits={adminStats.b2cExits}
            adminCtaClicks={adminStats.ctaClicks}
            topVariant={topVariant}
          />
        </div>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setResetConfirm(true)}
          disabled={isResetting || totalImpressions === 0}
          className="ml-4"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Resetuj statystyki
        </Button>
      </div>

      <HeadlineAIGenerator />

      <HeadlineDefaultTagManager allTags={allTags} />

      <HeadlineTagStats tagStats={tagStats} />

      {/* Filter by tag */}
      <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Filtruj po tagu:</span>
          <Select value={selectedTag} onValueChange={setSelectedTag}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Wszystkie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie warianty</SelectItem>
              {allTags.map(tag => (
                <SelectItem key={tag} value={tag}>{tag}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selectedTag !== "all" && (
          <span className="text-sm text-muted-foreground">
            Pokazano {filteredAndSortedStats.length} z {stats.length} wariantów
          </span>
        )}
      </div>

      {/* Top scrollbar */}
      <div 
        ref={topScrollRef}
        className="overflow-x-auto overflow-y-hidden"
        onScroll={(e) => {
          if (tableScrollRef.current) {
            tableScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
          }
        }}
      >
        <div style={{ width: '1400px', height: '1px' }} />
      </div>

      {/* Table with bottom scrollbar */}
      <div 
        ref={tableScrollRef}
        className="rounded-lg border overflow-x-auto"
        onScroll={(e) => {
          if (topScrollRef.current) {
            topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
          }
        }}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nagłówek</TableHead>
              <TableHead>Tagi</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('impressions')}
                  className="hover:bg-transparent font-medium"
                >
                  Wyświetlenia
                  {getSortIcon('impressions')}
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('conversions')}
                  className="hover:bg-transparent font-medium"
                >
                  Konwersje (Total/Quiz/Lead)
                  {getSortIcon('conversions')}
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('conversion_rate')}
                  className="hover:bg-transparent font-medium"
                >
                  CR %
                  {getSortIcon('conversion_rate')}
                </Button>
              </TableHead>
              <TableHead>Link kampanii</TableHead>
              <TableHead className="text-right">Akcje</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAndSortedStats.map((stat) => (
              <HeadlineVariantRow
                key={stat.id}
                variant={stat}
                onToggleActive={handleToggleActive}
                onDelete={(id) => setDeleteConfirm(id)}
                onEdit={handleEdit}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Czy na pewno chcesz usunąć ten wariant?</AlertDialogTitle>
            <AlertDialogDescription>
              Ta operacja jest nieodwracalna. Wszystkie dane związane z tym wariantem zostaną usunięte.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Usuń
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resetConfirm} onOpenChange={setResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resetować wszystkie statystyki A/B testów?</AlertDialogTitle>
            <AlertDialogDescription>
              Spowoduje to wyzerowanie wszystkich wyświetleń i konwersji. Warianty nagłówków NIE zostaną usunięte. Ta operacja jest nieodwracalna.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}>Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetStats}
              disabled={isResetting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isResetting ? "Resetowanie..." : "Resetuj statystyki"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};